import { useRef, useState, useCallback, useEffect } from "react";
import {
  Box,
  Editor,
  type TLShape,
  type TLShapeId,
  type TLStore,
} from "tldraw";
import type { WriteMode } from "../store/useSpaceStore";
import {
  applyDiffFilter,
  isInSyncRemove,
  type CollabMode,
} from "../spaces-diff-filter";
import { SHARED_LAYER } from "../constants";
import { convertTldrawShapesToPeripheralShapes } from "@/shared/format/convertTldrawShapesToPeripheralShapes";
import { SLIDE_EMBED_TYPE } from "../shapes/SlideEmbedShapeUtil";
import { canvasToDataUrl, rasterizeWithBlackInk } from "@/canvas/ink/rasterize";

// Maximum pixel dimension for canvas screenshots sent to the LLM vision API.
const MAX_SCREENSHOT_PX = 3072;

/**
 * Full canvas context snapshot sent to the spaces AI agent.
 *
 * blurryShapes     — ALL shapes the AI should "see" in detail:
 *                    • Every shape inside the viewport (full detail).
 *                    • Every embed (slide) shape anywhere on the canvas —
 *                      in-viewport gets full slideContent, off-screen gets
 *                      { outOfView: true, summary: "..." } so the AI still
 *                      knows lesson content even when the slide is scrolled away.
 *
 * viewport         — bounding box of what the student currently sees (page-space).
 *                    Lets the AI anchor its spatial reasoning to the student's view.
 *
 * selectedShapeIds — IDs (without "shape:" prefix) of shapes currently selected.
 *                    Lets the AI understand what the student is focusing on.
 *
 * peripheralShapes — spatial clusters of NON-embed shapes outside the viewport.
 *                    Only bounds + count per cluster (no text/details) to save tokens.
 *                    Embed shapes are excluded here — they appear in blurryShapes
 *                    with a text summary instead, preserving lesson context.
 */
export type SpaceCanvasContext = {
  blurryShapes: { type: "blurryShapes"; shapes: unknown[] } | null;
  userViewportBounds: {
    type: "userViewportBounds";
    userBounds: { x: number; y: number; w: number; h: number };
  } | null;
  selectedShapes: { type: "selectedShapes"; shapeIds: string[] } | null;
  peripheralShapes: {
    type: "peripheralShapes";
    clusters: {
      bounds: { x: number; y: number; w: number; h: number };
      numberOfShapes: number;
    }[];
  } | null;
};

type TLCameraState = ReturnType<Editor["getCamera"]>;

export function useStudentSpaceCanvases(
  userId?: number,
  isTeacher?: boolean,
  writeMode: WriteMode = "read",
  collabMode: CollabMode = "local",
  spaceInactive: boolean = false,
  savedCamera?: TLCameraState | null,
  personalSyncStore?: TLStore | null,
) {
  const whiteboardEditorRef = useRef<Editor | null>(null);
  // Bumped on each mount so effects keyed off it re-run when the editor is
  // (re)created — refs alone don't trigger React deps, so dependent effects
  // were missing the case where the editor ref appeared after their first run.
  const [editorMountTick, setEditorMountTick] = useState(0);
  const whiteboardUnlistenRef = useRef<(() => void) | null>(null);
  // True when the visible content has changed since the last screenshot:
  //   • a shape was drawn/edited/deleted inside the current viewport
  //   • the camera panned and the set of visible shapes is now different
  // When false, getCanvasScreenshot skips the expensive toImage call and returns
  // null (caller omits the screenshot part). Resets to true after each successful
  // capture, and starts true so the first turn always includes a screenshot.
  // Ref is read inside the async getCanvasScreenshot callback (no stale closure).
  const hasViewportChangedRef = useRef(true);
  const canvasDirtyUnsubRef = useRef<(() => void) | null>(null);
  const cameraDebouncerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Shape IDs visible in the viewport at the time of the last successful
  // screenshot — used to decide whether a camera pan actually revealed new content.
  const lastScreenshotShapeIdsRef = useRef<Set<TLShapeId>>(new Set());
  const personalUnlistenRef = useRef<(() => void) | null>(null);
  const personalStoreUnlistenRef = useRef<(() => void) | null>(null);

  const personalSyncStoreRef = useRef<TLStore | null>(
    personalSyncStore ?? null,
  );
  personalSyncStoreRef.current = personalSyncStore ?? null;

  const userMetaRef = useRef<{ userId: number; isTeacher: boolean } | null>(
    null,
  );
  useEffect(() => {
    if (userId !== undefined && isTeacher !== undefined) {
      userMetaRef.current = { userId, isTeacher };
    }
  }, [userId, isTeacher]);

  // Unsubscribe the dirty-tracking store listener and clear debounce on unmount.
  useEffect(
    () => () => {
      canvasDirtyUnsubRef.current?.();
      if (cameraDebouncerRef.current) clearTimeout(cameraDebouncerRef.current);
    },
    [],
  );

  const writeModeRef = useRef<WriteMode>(writeMode);
  writeModeRef.current = writeMode;

  const collabModeRef = useRef<CollabMode>(collabMode);
  collabModeRef.current = collabMode;

  const spaceInactiveRef = useRef<boolean>(spaceInactive);
  spaceInactiveRef.current = spaceInactive;

  // Re-apply diff filter whenever writeMode, collabMode, or spaceInactive changes after mount.
  // In local-edit mode the routing patch needs the personal sync store to be live; until it
  // is, force read-mode so any drawing is blocked rather than silently lost. Once the personal
  // store transitions to non-null this effect re-runs and installs the routing patch.
  useEffect(() => {
    const editor = whiteboardEditorRef.current;
    if (!editor) return;

    personalUnlistenRef.current?.();
    personalUnlistenRef.current = null;
    personalStoreUnlistenRef.current?.();
    personalStoreUnlistenRef.current = null;

    const pStore = personalSyncStoreRef.current;
    const personalNeeded = writeMode === "edit" && collabMode === "local";
    const personalReady = personalNeeded ? pStore !== null : true;

    // Mirror records from the personal sync store into editor.store so the
    // student SEES their personal shapes (teacher annotations, AI shapes,
    // prior-session shapes restored from snapshot). This must run regardless
    // of writeMode — read-mode students still need to view what's there;
    // their write rights are enforced separately by applyDiffFilter below.
    if (pStore) {
      editor.store.mergeRemoteChanges(() => {
        const initial = pStore
          .allRecords()
          .filter(
            (r: { typeName?: string } | null | undefined) =>
              r?.typeName === "shape" ||
              r?.typeName === "binding" ||
              r?.typeName === "asset",
          );
        if (initial.length) editor.store.put(initial);
      });
      const unsub = pStore.listen(({ changes }) => {
        editor.store.mergeRemoteChanges(() => {
          const { added, updated, removed } = changes;
          if (Object.keys(added).length) editor.store.put(Object.values(added));
          if (Object.keys(updated).length)
            editor.store.put(Object.values(updated).map(([, next]) => next));
          if (Object.keys(removed).length)
            editor.store.remove(Object.keys(removed) as any);
        });
      });
      personalStoreUnlistenRef.current = unsub;
    }

    // isReadonly blocks shape creation but leaves the current tool alone, so
    // the pen is parked on select for read mode and handed back on edit —
    // otherwise the student sits on a draw tool whose strokes go nowhere.
    const wasReadonly = editor.getIsReadonly();
    if (spaceInactive || writeMode === "read" || !personalReady) {
      const cleanup = applyDiffFilter(editor, "read", collabMode);
      if (cleanup) personalUnlistenRef.current = cleanup;
      editor.updateInstanceState({ isReadonly: true });
      if (editor.getCurrentToolId() === "draw") editor.setCurrentTool("select");
      return;
    }

    const uid = String(userMetaRef.current?.userId ?? "");
    const cleanup = applyDiffFilter(editor, writeMode, collabMode, pStore, uid);
    if (cleanup) personalUnlistenRef.current = cleanup;
    editor.updateInstanceState({ isReadonly: false });
    if (wasReadonly) editor.setCurrentTool("draw");
  }, [
    writeMode,
    collabMode,
    spaceInactive,
    personalSyncStore,
    editorMountTick,
  ]);

  // Sync into a ref so onWhiteboardMount sees the latest value without
  // re-creating the callback (and re-running tldraw mount) on every prop change.
  const savedCameraRef = useRef<TLCameraState | null>(savedCamera ?? null);
  savedCameraRef.current = savedCamera ?? null;

  const onWhiteboardMount = useCallback((editor: Editor) => {
    editor.setCameraOptions({ zoomSteps: [1] });
    editor.updateInstanceState({ isGridMode: true });
    // Pen in hand from the start; the writeMode effect parks it for read mode.
    editor.setCurrentTool("draw");
    whiteboardUnlistenRef.current?.();

    whiteboardEditorRef.current = editor;
    // Trigger the personal-sync effect to re-run now that the editor exists —
    // ref changes don't fire React deps on their own, so without this the
    // effect can run with editor=null first and never get a chance to install
    // the inject + listener once the editor mounts later.
    setEditorMountTick((n) => n + 1);

    // Mark canvas dirty when the visible viewport content changes:
    //   • camera panned → different shapes in view even if nothing was drawn
    //   • shape added/updated that intersects the current viewport
    //   • shape deleted (bounds unavailable post-deletion, mark dirty conservatively)
    // Reset dirty state on remount so the first getCanvasScreenshot after a
    // remount always captures a fresh screenshot regardless of previous state.
    hasViewportChangedRef.current = true;
    lastScreenshotShapeIdsRef.current = new Set();
    canvasDirtyUnsubRef.current?.();
    canvasDirtyUnsubRef.current = editor.store.listen(
      ({ changes }) => {
        // Camera pan → debounce then compare visible shape IDs against the
        // last screenshot's baseline. If the set changed → dirty; if the user
        // panned back to the same view → reset to clean so no redundant capture.
        const cameraChanged = Object.values(changes.updated).some(
          ([, next]) => next.typeName === "camera",
        );
        if (cameraChanged) {
          if (cameraDebouncerRef.current)
            clearTimeout(cameraDebouncerRef.current);
          cameraDebouncerRef.current = setTimeout(() => {
            cameraDebouncerRef.current = null;
            const viewport = editor.getViewportPageBounds();
            const currentIds = new Set(
              editor
                .getCurrentPageShapesSorted()
                .filter((s) => {
                  const b = editor.getShapeMaskedPageBounds(s);
                  return b && viewport.collides(b);
                })
                .map((s) => s.id),
            );
            const prev = lastScreenshotShapeIdsRef.current;
            const sameSet =
              currentIds.size === prev.size &&
              [...currentIds].every((id) => prev.has(id));
            hasViewportChangedRef.current = !sameSet;
          }, 150);
          return;
        }

        // Shape removed — can't compute bounds after deletion; mark dirty
        const hasRemovedShapes = Object.values(changes.removed).some(
          (r) => r.typeName === "shape",
        );
        if (hasRemovedShapes) {
          hasViewportChangedRef.current = true;
          return;
        }

        // Shape added/updated — only dirty if it overlaps the current viewport
        const viewport = editor.getViewportPageBounds();
        const affectedIds = [
          ...Object.values(changes.added)
            .filter((r) => r.typeName === "shape")
            .map((r) => r.id as TLShapeId),
          ...Object.values(changes.updated)
            .map(([, next]) => next)
            .filter((r) => r.typeName === "shape")
            .map((r) => r.id as TLShapeId),
        ];
        const inViewport = affectedIds.some((id) => {
          const bounds = editor.getShapeMaskedPageBounds(id);
          return bounds && viewport.collides(bounds);
        });
        if (inViewport) {
          hasViewportChangedRef.current = true;
        }
      },
      { source: "all" },
    );

    // Restore camera position/zoom from the student's last saved snapshot.
    // Personal shapes are no longer restored here — they arrive through the
    // personal TLSync channel automatically when using the multiplexed endpoint.
    if (savedCameraRef.current) {
      editor.setCamera(savedCameraRef.current, { animation: { duration: 0 } });
    }

    editor.sideEffects.registerBeforeChangeHandler(
      "shape",
      (prev, next, source) => {
        if (source !== "user") return next;
        if ((next as any).type === SLIDE_EMBED_TYPE) return prev;
        // Students cannot mutate teacher-authored shapes (e.g. annotations the
        // teacher drew on this student's personal canvas while watching). The
        // teacher's editor is exempt — it has its own guard in StudentWorkCanvas.
        if (userMetaRef.current?.isTeacher) return next;
        const meta = (next.meta ?? {}) as Record<string, unknown>;
        if (meta.role === "teacher") return prev;
        return next;
      },
    );

    editor.sideEffects.registerBeforeDeleteHandler("shape", (shape) => {
      const store = editor.store as any;
      // In local collabMode the diff-filter patch is active and sets _isInSyncRemove=true
      // only for server-originated removes (local user removes are also wrapped in
      // mergeRemoteChanges by the patch, so isMergingRemoteChanges alone is not enough).
      // In edit+shared mode there is no patch, so TLDraw/sync's own isMergingRemoteChanges
      // is the right indicator: it's true only when the sync server sends the update.
      const patchActive = !!store.__diffFilterCleanup;
      const inSyncRemove = patchActive
        ? isInSyncRemove()
        : store.isMergingRemoteChanges === true;
      const meta = shape.meta as Record<string, unknown>;
      // Sync-originated removes always pass through — if the sender's handler
      // allowed it, the server broadcast it, so we must apply it too.
      if (inSyncRemove) return;
      // Imported slides are immutable for everyone except the importing teacher.
      if ((shape as any).type === SLIDE_EMBED_TYPE) return false;
      if (userMetaRef.current?.isTeacher) return;
      // Students cannot delete teacher-authored shapes.
      if (meta?.role === "teacher") return false;
      // Own personal shapes (layer === userId) are deletable.
      const uid = String(userMetaRef.current?.userId ?? "");
      if (meta?.layer === uid) return;
      // Legacy fallback: pre-meta.layer snapshots used isPersonal. Block any
      // non-personal shape from being deleted in local mode (it would belong
      // to the shared canvas and the deletion would desync).
      if (collabModeRef.current === "local" && meta?.isPersonal !== true)
        return false;
    });

    editor.sideEffects.registerBeforeCreateHandler("shape", (shape, source) => {
      if (source !== "user") return shape;
      if (!userMetaRef.current) return shape;
      // Pre-stamped role (e.g. AI-emitted shapes that mark themselves as
      // "spaces-agent") wins — we don't downgrade them to plain student shapes.
      const existingMeta = (shape.meta ?? {}) as Record<string, unknown>;
      if (
        typeof existingMeta.role === "string" &&
        existingMeta.role !== "student"
      ) {
        return shape;
      }
      const { userId, isTeacher } = userMetaRef.current;
      const isLocal = collabModeRef.current === "local";
      return {
        ...shape,
        meta: {
          ...(shape.meta as object),
          role: isTeacher ? "teacher" : "student",
          createdBy: userId,
          layer:
            isTeacher || collabModeRef.current === "shared"
              ? SHARED_LAYER
              : String(userId),
          ...(isLocal ? { isPersonal: true } : {}),
        },
      };
    });

    // Filter / pStore wiring is owned by the writeMode/collabMode effect above —
    // it re-runs as soon as setEditorMountTick fires, so we don't need to
    // duplicate the inject + listener here. Mounting only registers side
    // effect handlers and bumps the tick.
  }, []);

  /** Collect full canvas context for the spaces AI agent. */
  const getCanvasContext = useCallback((): SpaceCanvasContext => {
    const editor = whiteboardEditorRef.current;
    if (!editor)
      return {
        blurryShapes: null,
        userViewportBounds: null,
        selectedShapes: null,
        peripheralShapes: null,
      };

    const currentUserId = userMetaRef.current?.userId;
    const viewportBounds = editor.getViewportPageBounds();

    // ── Part 1: userViewportBounds ────────────────────────────────────────────
    // Page-space bounding box of what the student currently sees.
    const userViewportBounds = {
      type: "userViewportBounds" as const,
      userBounds: {
        x: Math.round(viewportBounds.x),
        y: Math.round(viewportBounds.y),
        w: Math.round(viewportBounds.w),
        h: Math.round(viewportBounds.h),
      },
    };

    // ── Part 2: selectedShapes ────────────────────────────────────────────────
    // Currently selected shape IDs stripped of the "shape:" prefix.
    const selectedIds = editor
      .getSelectedShapes()
      .map((s) => s.id.replace(/^shape:/, ""));
    const selectedShapes =
      selectedIds.length > 0
        ? { type: "selectedShapes" as const, shapeIds: selectedIds }
        : null;

    // ── Parts 3 & 4: blurryShapes + peripheralShapes ─────────────────────────
    // Single pass over all shapes: in-viewport → blurryShapes (full detail);
    // embed shapes always → blurryShapes regardless of position (lesson content);
    // non-embed off-screen → outsideNonEmbedShapes → clustered → peripheralShapes.
    const blurryShapeEntries: unknown[] = [];
    const outsideNonEmbedShapes: TLShape[] = [];

    for (const shape of editor.getCurrentPageShapesSorted()) {
      const bounds = editor.getShapeMaskedPageBounds(shape);
      if (!bounds) continue;

      const util = editor.getShapeUtil(shape);
      const meta = shape.meta as Record<string, unknown>;
      const storedRole = meta?.role as string | undefined;
      const createdBy = meta?.createdBy as number | undefined;
      const role =
        storedRole === "student" &&
        createdBy !== undefined &&
        createdBy !== currentUserId
          ? "other-student"
          : storedRole;

      const inViewport = viewportBounds.collides(bounds);

      if ((shape as any).type === SLIDE_EMBED_TYPE) {
        // Embed (slide) shapes always go into blurryShapes regardless of position.
        // meta.slideContent is a SlideElement[] set once at import time by importLessonSlides
        // via parseSlideHtml — never modified after creation.
        // In-viewport → full lesson content; off-screen → text summary only.
        const rawContent = meta?.slideContent as Array<{
          type: string;
          text?: string;
          latex?: string;
        }> | null;
        let slideContent: unknown;
        if (inViewport) {
          slideContent = rawContent
            ? { coords: "iframe-relative-960x700", elements: rawContent }
            : null;
        } else {
          const textSummary = rawContent
            ?.filter(
              (el) =>
                (el.type === "text" || el.type === "math") &&
                (el.text ?? el.latex),
            )
            .map((el) => el.text ?? el.latex)
            .join("\n");
          slideContent = textSummary
            ? { outOfView: true, summary: textSummary }
            : { outOfView: true };
        }
        blurryShapeEntries.push({
          shapeId: shape.id.replace(/^shape:/, ""),
          type: shape.type,
          ...(role ? { role } : {}),
          ...(slideContent ? { slideContent } : {}),
          x: Math.round(bounds.x),
          y: Math.round(bounds.y),
          w: Math.round(bounds.w),
          h: Math.round(bounds.h),
        });
        continue;
      }

      if (inViewport) {
        // Non-embed shape in view → full detail in blurryShapes.
        const text = (util.getText(shape) ?? "").trim();
        blurryShapeEntries.push({
          shapeId: shape.id.replace(/^shape:/, ""),
          type: shape.type,
          ...(text ? { text } : {}),
          ...(role ? { role } : {}),
          x: Math.round(bounds.x),
          y: Math.round(bounds.y),
          w: Math.round(bounds.w),
          h: Math.round(bounds.h),
        });
      } else {
        // Non-embed shape off-screen → queue for spatial clustering.
        outsideNonEmbedShapes.push(shape);
      }
    }

    // Cluster off-screen non-embed shapes with 75px padding (same as canvas agent).
    // Each cluster: bounds + count only — no shape details, saves tokens.
    const clusters = convertTldrawShapesToPeripheralShapes(
      editor,
      outsideNonEmbedShapes,
      { padding: 75 },
    );
    const peripheralShapes =
      clusters.length > 0
        ? {
            type: "peripheralShapes" as const,
            clusters: clusters.map((c) => ({
              bounds: {
                x: Math.round(c.bounds.x),
                y: Math.round(c.bounds.y),
                w: Math.round(c.bounds.w),
                h: Math.round(c.bounds.h),
              },
              numberOfShapes: c.numberOfShapes,
            })),
          }
        : null;

    return {
      blurryShapes:
        blurryShapeEntries.length > 0
          ? { type: "blurryShapes" as const, shapes: blurryShapeEntries }
          : null,
      userViewportBounds,
      selectedShapes,
      peripheralShapes,
    };
  }, []);

  /** Take a JPEG screenshot of the shared canvas viewport.
   *  Returns null (skip screenshot) when the visible content has not changed
   *  since the last successful capture — avoids redundant expensive toImage
   *  calls and saves vision tokens on the LLM side.
   *
   *  `blurryShapes` (computed in getCanvasContext) is always sent regardless —
   *  the gateway does NOT cache it between turns, so omitting it would cause
   *  the LLM to receive "There are no shapes in your view" even when there are. */
  const getCanvasScreenshot = useCallback(async (): Promise<{
    type: "screenshot";
    screenshot: string;
  } | null> => {
    if (!hasViewportChangedRef.current) return null;

    const editor = whiteboardEditorRef.current;
    if (!editor) return null;

    const viewportBounds = editor.getViewportPageBounds();
    const viewportBox = Box.From(viewportBounds);

    const shapes = editor.getCurrentPageShapesSorted().filter((shape) => {
      const bounds = editor.getShapeMaskedPageBounds(shape);
      if (!bounds) return false;
      return viewportBox.includes(bounds);
    });

    if (shapes.length === 0) {
      hasViewportChangedRef.current = false;
      lastScreenshotShapeIdsRef.current = new Set();
      return null;
    }

    const largestDimension = Math.max(viewportBounds.w, viewportBounds.h);
    const scale =
      largestDimension > MAX_SCREENSHOT_PX
        ? MAX_SCREENSHOT_PX / largestDimension
        : 1;

    // Layered so the handwriting flattens to black. Teacher ink keeps its
    // colour — `isStudentInk` excludes it.
    const raster = await rasterizeWithBlackInk(shapes, {
      bounds: viewportBox,
      scale,
      outputW: Math.round(viewportBounds.w * scale),
      outputH: Math.round(viewportBounds.h * scale),
      renderBatch: (batch, imageOpts) => editor.toImage(batch, imageOpts),
      // Yield between layers so audio callbacks can fire through a render.
      onBatchBoundary: () =>
        new Promise<void>((resolve) => setTimeout(resolve, 0)),
    });
    // `null` is this function's "no screenshot this turn" signal; the notepad's
    // part is mandatory, so it sends `screenshot: ""` instead.
    if (!raster) return null;

    const dataUrl = await canvasToDataUrl(raster.ctx.canvas);
    // Reset dirty flag only after a successful capture so a failed render
    // still retries on the next turn. Save the current viewport shape IDs so
    // a subsequent camera pan can compare against this baseline.
    hasViewportChangedRef.current = false;
    lastScreenshotShapeIdsRef.current = new Set(shapes.map((s) => s.id));
    return { type: "screenshot", screenshot: dataUrl };
  }, []);

  /** Return the shared canvas editor instance for the spaces agent. */
  const getEditor = useCallback(
    (): Editor | null => whiteboardEditorRef.current,
    [],
  );

  /** Capture current camera position/zoom for snapshot save. */
  const getCamera = useCallback((): TLCameraState | null => {
    return whiteboardEditorRef.current?.getCamera() ?? null;
  }, []);

  return {
    onWhiteboardMount,
    getEditor,
    getCamera,
    getCanvasContext,
    getCanvasScreenshot,
  };
}
